import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createInnovationApi,
  deleteInnovationApi,
  fetchInnovation,
  fetchInnovations,
  updateInnovationApi,
  validateInnovationApi,
} from "../services/innovationsService";

const INNOVATIONS_QUERY_KEY = ["innovations"];

export const useInnovations = () =>
  useQuery({
    queryKey: INNOVATIONS_QUERY_KEY,
    queryFn: () => fetchInnovations(),
  });

export const useInnovation = (id, options = {}) =>
  useQuery({
    queryKey: [...INNOVATIONS_QUERY_KEY, id],
    queryFn: () => fetchInnovation(id),
    enabled: Boolean(id),
    ...options,
  });

export const useCreateInnovation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInnovationApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INNOVATIONS_QUERY_KEY });
      toast.success("Innovation bien ajoutée", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Innovation non ajoutée";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useUpdateInnovation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateInnovationApi(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: INNOVATIONS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...INNOVATIONS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Innovation bien modifié", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Innovation non modifié";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useValidateInnovation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => validateInnovationApi(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: INNOVATIONS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...INNOVATIONS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Innovation validée", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Innovation non validé";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useDeleteInnovation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteInnovationApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INNOVATIONS_QUERY_KEY });
      toast.success("Innovation supprimée avec succès", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg =
        err?.message ||
        "Une erreur est survenue lors de la suppression de l'innovation";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};
